import { Typography } from "@/components/atoms";
import { BedIcon, ExpandIcon, ShowerHeadIcon } from "@/components/atoms/icons";
import { CurrencyIcon } from "@/components/atoms/icons/currencyicon";
import { ImageCarousel } from "@/components/molecules/ImageCarousel";
import { useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { hasNumericValue, hasTextValue } from "@/core/utils/fieldAvailability";
import React from "react";
import { Platform, Pressable, StyleSheet, View } from "react-native";
import { usePropertyLabels } from '../hooks/usePropertyLabels';

export interface ListingCardProps {
  images?: { uri: string }[];
  /** Image list still resolving — carousel shows shimmer instead of the placeholder. */
  imagesLoading?: boolean;
  title: string;
  bedrooms?: number | undefined;
  bathrooms?: number | undefined;
  sqft?: number | undefined;
  price?: number | undefined;
  handoverDate?: string | undefined;
  onPress?: () => void;
  /**
   * Fill parent column width (guest project-card behaviour).
   * When false, card stays capped at 443px.
   */
  readonly fullWidth?: boolean;
  /** One card per row on medium/tablet — taller media band. */
  readonly singleColumn?: boolean;
}

const LISTING_CARD_MEDIA_HEIGHT = 200;
const LISTING_CARD_MEDIA_HEIGHT_SINGLE = 260;

function ListingCardComponent({
  images,
  imagesLoading = false,
  title,
  bedrooms,
  bathrooms,
  sqft,
  price,
  handoverDate,
  onPress,
  fullWidth = false,
  singleColumn = false,
}: Readonly<ListingCardProps>) {
  const c = useUIColors();
  const { isRTL } = useI18nContext();
  const { listing: LABELS } = usePropertyLabels();
  const mediaHeight = singleColumn
    ? LISTING_CARD_MEDIA_HEIGHT_SINGLE
    : LISTING_CARD_MEDIA_HEIGHT;

  const specs = [
    hasNumericValue(bedrooms)
      ? {
          key: "bedrooms",
          icon: <BedIcon size={18} color={c.fontTertiary} />,
          value: String(bedrooms),
          label: LABELS.bedrooms,
        }
      : null,
    hasNumericValue(bathrooms)
      ? {
          key: "bathrooms",
          icon: <ShowerHeadIcon size={18} color={c.fontTertiary} />,
          value: String(bathrooms),
          label: LABELS.bathrooms,
        }
      : null,
    hasNumericValue(sqft)
      ? {
          key: "sqft",
          icon: <ExpandIcon size={18} color={c.fontTertiary} />,
          value: new Intl.NumberFormat().format(sqft),
          label: LABELS.sqft,
        }
      : null,
  ].filter((spec): spec is NonNullable<typeof spec> => spec !== null);

  const showPrice = hasNumericValue(price);
  const showHandover = hasTextValue(handoverDate);

  return (
    <Pressable
      onPress={onPress}
      disabled={!onPress}
      accessibilityRole="button"
      style={({ pressed }) => [
        styles.container,
        fullWidth ? styles.containerFullWidth : styles.containerInGrid,
        {
          backgroundColor: "#FFFFFF",
          borderColor: c.outlineInactive,
          opacity: onPress && pressed ? 0.92 : 1,
        },
      ]}
    >
      <View style={[styles.imageWrap, { height: mediaHeight }]}>
        <ImageCarousel
          images={images ?? []}
          height={mediaHeight}
          paginationVariant="listing"
          loading={imagesLoading}
          disableAutoScroll
          {...(onPress ? { onPress } : {})}
        />
      </View>

      <View style={styles.content}>
        <Typography color="primary" numberOfLines={2} style={{ fontSize: 20, fontWeight: "500", fontFamily: 'Matter-Medium', lineHeight: 25, textAlign: isRTL ? 'right' : 'left' }}>
          {title}
        </Typography>

        {specs.length > 0 ? (
          <View style={styles.specsRow}>
            {specs.map((spec) => (
              <View
                key={spec.key}
                style={[
                  styles.specItem,
                  // Equal share of the row: 1 → full, 2 → ½, 3 → ⅓.
                  { flexGrow: 1, flexShrink: 1, flexBasis: 0 },
                ]}
              >
                {spec.icon}
                <View style={styles.specText}>
                  <Typography
                    color="primary"
                    style={styles.specValue}
                    numberOfLines={1}
                  >
                    {spec.value}
                  </Typography>
                  <Typography
                    color="tertiary"
                    style={styles.specLabel}
                    numberOfLines={1}
                  >
                    {spec.label}
                  </Typography>
                </View>
              </View>
            ))}
          </View>
        ) : null}
      </View>

      {showPrice || showHandover ? (
        <View
          style={[
            styles.footer,
            {
              backgroundColor: c.backgroundLevel2,
              flexDirection: "row",
            },
          ]}
        >
          {showPrice ? (
            <View style={styles.priceRow}>
              <CurrencyIcon size={16} color={c.fontPrimary} />
              <Typography color="primary" style={{ fontSize: 18, fontWeight: '500', fontFamily: 'Matter-Medium' }}>
                {new Intl.NumberFormat("en-AE").format(price)}
              </Typography>
            </View>
          ) : null}

          {showHandover ? (
            <View style={styles.handoverCol}>
              <Typography color="tertiary" style={{ fontSize: 14 }}>
                {LABELS.handover} {handoverDate}
              </Typography>
            </View>
          ) : null}
        </View>
      ) : null}
    </Pressable>
  );
}

export const ListingCard = React.memo(ListingCardComponent);

const styles = StyleSheet.create({
  container: {
    borderRadius: 12,
    borderWidth: 0,
    padding: 8,
    width: "100%",
    minWidth: 0,
    flexDirection: 'column',
    gap: 8,
  },
  /** Listing grid — stretch to tallest sibling; keep Figma card cap. */
  containerInGrid: {
    flex: 1,
    maxWidth: 443,
  },
  /** Fill parent column (ADHA explore / guest-style) — no 443 cap. */
  containerFullWidth: {
    maxWidth: '100%',
    width: '100%',
    alignSelf: 'stretch',
  },
  imageWrap: {
    width: "100%",
    borderRadius: 8,
    overflow: "hidden",
    position: "relative",
  },
  content: {
    paddingHorizontal: 12,
    paddingTop: 10,
    paddingBottom: 10,
    flex: 1,
    gap: 12,
    justifyContent: 'flex-start',
  },
  /** Specs share the row equally (1 full / 2 halves / 3 thirds). */
  specsRow: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
  },
  specItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    minWidth: 0,
    paddingRight: 8,
  },
  specText: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    flexShrink: 1,
    minWidth: 0,
  },
  specValue: {
    fontSize: 14,
    lineHeight: 18,
    letterSpacing: 0,
    // Named face already carries weight — do not also set fontWeight or web
    // synthesizes a second bold and the numbers look blurry/black.
    fontFamily: "Matter-SemiBold",
    fontWeight: Platform.OS === "web" ? "400" : "600",
    flexShrink: 0,
  },
  specLabel: {
    fontSize: 14,
    lineHeight: 18,
    fontFamily: "Matter-Regular",
    fontWeight: "400",
    flexShrink: 1,
    minWidth: 0,
  },
  footer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    justifyContent: "space-between",
    alignItems: "center",
    borderRadius: 8,
    marginHorizontal: 12,
    marginBottom: 0,
    flexWrap: "wrap",
    gap: 8,
  },
  priceRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  handoverCol: {
    alignItems: "flex-end",
  },
});
